import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { portalAuthAPI } from '../services/portalAPI';

interface TaxExemptionStatus {
  is_exempt: boolean;
  certificate: {
    id: number;
    expiration_date: string;
    certificate_number: string;
    status: string;
  } | null;
  message: string;
  warning: string | null;
}

interface PortalCustomer {
  id: number;
  name: string;
  display_name?: string;  // Public store name for header display
  public_store_name?: string;
  customer_type: string;
  is_tax_exempt: boolean;
  tax_exemption_status?: TaxExemptionStatus;  // Detailed status for warnings
  bridge_club_enabled?: boolean;
  farmhand_activated?: boolean;
  // Address info for Farm Map auto-initialization
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface PortalPermissions {
  can_place_orders: boolean;
  can_view_invoices: boolean;
  // Farm-specific permissions (wholesale customers)
  farm_role?: string | null;
  can_manage_farm_employees?: boolean;
  can_create_tasks?: boolean;
  can_complete_tasks?: boolean;
  can_edit_farm_map?: boolean;
  can_log_movements?: boolean;
  can_create_movement_plans?: boolean;
}

interface PortalUser {
  id: number;
  email: string;
  name: string;
  customer: PortalCustomer;
  permissions: PortalPermissions;
}

interface PortalAuthContextType {
  user: PortalUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

// Customer Portal uses SEPARATE auth from Team portals
// Auth relies entirely on httpOnly cookies + /api/portal/auth/me/ response

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check auth by calling /api/portal/auth/me/ (cookies sent automatically)
    const checkAuth = async () => {
      // Only check portal auth on portal routes
      if (!window.location.pathname.startsWith('/portal')) {
        setLoading(false);
        return;
      }
      // Timeout: if auth check takes >5s, bail to login
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timeout')), 5000)
      );
      try {
        const response = await Promise.race([portalAuthAPI.getMe(), timeout]);
        const userData = response.data;
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      // Login endpoint sets httpOnly cookies automatically
      const response = await portalAuthAPI.login(email, password);
      const { user: userData } = response;
      setUser(userData);
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await portalAuthAPI.logout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  }), [user, loading, login, logout]);

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return context;
};
